import {
  PERMISSION_ENTITY_INVOICE,
  PERMISSION_ENTITY_INVOICE_OFFER,
  PERMISSION_ENTITY_OPERATIONAL,
  PERMISSION_ENTITY_PROOF_OF_ACCEPTANCE,
  PERMISSION_ENTITY_PURCHASE_ORDER
} from '@klear/constants'
import type { CurrencyCode } from '@klear/constants/currency'
import { KlearDate } from '@klear/date'
import { Money, dominantCurrencyTotal, formatPerCurrencyTotals, perCurrencyTotals, sumByCurrency } from '@klear/money'
import type { OrderEventDomainListItem } from '@klear/order-event/order-event-domain-list'
import { ORDER_EVENT_STATUS_REJECTED, ORDER_EVENT_STATUS_VOIDED } from '@klear/constants/order-event-status'

export type DateDisplayInput = KlearDate | string

export function moniesFromDisplayInputs(inputs: readonly (Money | null | undefined)[]): Money[] {
  return inputs.filter((money): money is Money => money != null)
}

export function filterMoniesByCurrency(monies: readonly Money[], currency: CurrencyCode): Money[] {
  return monies.filter((m) => m.currency === currency)
}

type PurchaseOrderVolumeRow = {
  status: string
  customerId?: number | null
  amount: Money
}

export type PurchaseOrderVolumeOptions = {
  currency?: CurrencyCode
}

export function purchaseOrderVolumeFromAmounts(amounts: readonly Money[], options?: PurchaseOrderVolumeOptions): Money {
  const totals = sumByCurrency(amounts)
  const currency = options?.currency
  if (currency != null) {
    return totals.get(currency) ?? Money.zero(currency)
  }
  return dominantCurrencyTotal(totals)
}

export function purchaseOrderVolumeByCustomerId(
  purchaseOrders: readonly PurchaseOrderVolumeRow[],
  options?: PurchaseOrderVolumeOptions
): Map<number, Money> {
  const amountsByCustomerId = new Map<number, Money[]>()

  for (const po of purchaseOrders) {
    if (isListItemVoided(po.status) || isListItemRejected(po.status)) continue
    const customerId = po.customerId
    if (!customerId) continue
    const money = po.amount
    if (!money.greaterThan(money.zero)) continue
    const bucket = amountsByCustomerId.get(customerId) ?? []
    bucket.push(money)
    amountsByCustomerId.set(customerId, bucket)
  }

  const result = new Map<number, Money>()
  for (const [customerId, monies] of amountsByCustomerId) {
    result.set(customerId, purchaseOrderVolumeFromAmounts(monies, options))
  }
  return result
}

export function moneySumFormatted(monies: readonly (Money | null | undefined)[]): string | null {
  const parsed = moniesFromDisplayInputs(monies)
  if (parsed.length === 0) return null
  return formatPerCurrencyTotals(perCurrencyTotals(parsed))
}

type ListItemAmountInput = Money | { amount: Money }

export function listItemAmountFormatted(amountOrItem: ListItemAmountInput): string {
  return (amountOrItem instanceof Money ? amountOrItem : amountOrItem.amount).toString()
}

export function resolveDateDisplayInput(value: unknown): KlearDate | null {
  if (value == null) return null
  return KlearDate.parse(value as string | KlearDate) ?? null
}

export function dateFormatted(value: DateDisplayInput | undefined | null, format?: string): string | null {
  const parsed = resolveDateDisplayInput(value)
  if (!parsed) return null
  return format ? parsed.toString(format) : KlearDate.format(parsed)
}

export function dateFormattedOrDash(value: DateDisplayInput | undefined | null, format?: string): string {
  return dateFormatted(value, format) ?? '—'
}

export function listItemDateEpochDay(value: DateDisplayInput | undefined | null): number {
  return resolveDateDisplayInput(value)?.epochDay ?? 0
}

export function domainListItemAmountMoney(item: OrderEventDomainListItem): Money | null {
  switch (item.type) {
    case PERMISSION_ENTITY_INVOICE_OFFER:
      return item.model.purchasePrice
    case PERMISSION_ENTITY_INVOICE:
    case PERMISSION_ENTITY_PURCHASE_ORDER:
      return item.model.amount
    case PERMISSION_ENTITY_OPERATIONAL: {
      const money = item.model.amount
      return money.greaterThan(money.zero) ? money : null
    }
    case PERMISSION_ENTITY_PROOF_OF_ACCEPTANCE:
      return null
  }
}

export function domainListItemEffectiveDateResolved(item: OrderEventDomainListItem): KlearDate | null {
  return resolveDateDisplayInput(domainListItemEffectiveDate(item))
}

export function domainListItemEffectiveDate(item: OrderEventDomainListItem): DateDisplayInput | undefined {
  switch (item.type) {
    default:
      return item.model.effectiveDate
  }
}

export function domainListItemNumber(item: OrderEventDomainListItem): string | undefined {
  switch (item.type) {
    case PERMISSION_ENTITY_INVOICE:
    case PERMISSION_ENTITY_PURCHASE_ORDER:
    case PERMISSION_ENTITY_OPERATIONAL:
    case PERMISSION_ENTITY_INVOICE_OFFER:
      return item.model.number
    case PERMISSION_ENTITY_PROOF_OF_ACCEPTANCE:
      return undefined
  }
}

export function listItemEffectiveDate(
  item: Pick<{ effectiveDate: DateDisplayInput }, 'effectiveDate'> | { effectiveDate: DateDisplayInput }
): KlearDate | null {
  return resolveDateDisplayInput(item.effectiveDate)
}

export function listItemEndDate(
  item: Pick<{ endDate?: DateDisplayInput }, 'endDate'> | { endDate?: DateDisplayInput }
): KlearDate | null {
  return resolveDateDisplayInput(item.endDate)
}

export function isListItemVoided(status: string): boolean {
  return status === ORDER_EVENT_STATUS_VOIDED
}

export function isListItemRejected(status: string): boolean {
  return status === ORDER_EVENT_STATUS_REJECTED
}
